import { formatDate } from '@hqo/date-time/core';
import { formatLocaleTimeOrEmpty } from 'app/shared/utils/format-locale-time';
import { EventDate } from 'app/shared/types/event-date';

/**
 * A building with no timezone falls back to UTC, never the viewer's zone. Rendering an instant in
 * whatever zone the browser happens to be in shows the wrong calendar day either side of midnight, and
 * it does so differently for every viewer — UTC is at least wrong in one predictable direction, which
 * is the platform-wide decision for a null building timezone.
 */
const zoneOf = (timezone?: string): string => timezone || 'UTC';

/**
 * The zero-padded day and month used ONLY to decide whether a range spans two days (see
 * formatEventDateRange). They are comparison keys, not display, so they are formatted with a fixed
 * locale — but in the EVENT's zone, which is the part that used to be wrong: they came from a bare
 * `dayjs(value).format('DD')`, with no timezone at all, while every other field in this object was
 * formatted in the building's. For an event at 23:30 UTC in Europe/Berlin that meant `eventDateDay`
 * said 16 and `eventDateDayDD` said 15 — from the same call, and the range logic read the wrong one.
 */
const comparisonKey = (value: string, timezone: string, part: 'day' | 'month'): string =>
  new Intl.DateTimeFormat('en-US', { [part]: '2-digit', timeZone: timezone }).format(new Date(value));

/**
 * Content can carry a start with no end, and ContentView formats both unconditionally. The previous
 * implementation returned the literal string "Invalid Date" for every field, because
 * `Date.toLocaleString` does; `Intl.DateTimeFormat.format` and the package's time formatters instead
 * throw `RangeError: Invalid time value`, which would take the whole content view down. Empty fields
 * keep the old caller behaviour — formatEventDateRange compares them, finds them equal, and shows the
 * start date alone — without leaking "Invalid Date" into the UI.
 */
const EMPTY_EVENT_DATE: EventDate = {
  eventStartTime: '',
  eventDateMonth: '',
  eventDateMonthNum: '',
  eventDateDay: '',
  eventDateMonthLong: '',
  eventDateYear: '',
  eventDateMonthNumMM: '',
  eventDateDayDD: '',
  eventWeekdayLong: '',
  fullDate: '',
};

const isRealDate = (value: string): boolean => Boolean(value) && !Number.isNaN(new Date(value).getTime());

export const formatEventDate = (value: string, locale: string, timezone: string): EventDate => {
  if (!isRealDate(value)) {
    return EMPTY_EVENT_DATE;
  }

  const zone = zoneOf(timezone);
  const part = (options: Intl.DateTimeFormatOptions): string => formatDate(value, { locale, timezone: zone, options });

  return {
    eventDateMonth: part({ month: 'short' }),
    eventDateMonthNum: part({ month: 'numeric' }),
    eventDateDay: part({ day: 'numeric' }),
    eventDateMonthLong: part({ month: 'long' }),
    eventDateYear: part({ year: 'numeric' }),
    eventDateMonthNumMM: comparisonKey(value, zone, 'month'),
    eventDateDayDD: comparisonKey(value, zone, 'day'),
    eventWeekdayLong: part({ weekday: 'long' }),
    /**
     * Was `{ hour: '2-digit', minute: '2-digit', hourCycle: 'h24' }`, which was wrong twice. Forcing
     * h24 stripped the day period from the 12-hour locales — en, en-CA and en-AU rendered a bare
     * `01:30` with no AM/PM — and `h24` is the 1–24 cycle, so midnight printed as `24:00` rather than
     * `00:00` in every locale. formatLocaleTime lets each locale pick its own clock, and applies the
     * French `h` separator to BOTH French regions — CLDR ships `13 h 45` for fr-CA but plain `13:45`
     * for fr-FR, so without the override the two Frenches disagree on the same screen.
     */
    eventStartTime: formatLocaleTimeOrEmpty(new Date(value), locale, zone),
    fullDate: part({ weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
  };
};
